/**
 * Main product page — variant sync, cart fixes, subscription UI, reviews preserve
 */
(function () {
  const ROOT = '.mm-main-product';

  function getProductForm() {
    return document.querySelector(`${ROOT} form[id^="product-form-main-"]`);
  }

  function getRerenderRoot() {
    return document.querySelector(`${ROOT} product-rerender[allow-partial-rerender]`);
  }

  function getSubscriptionPicker() {
    return document.querySelector(`${ROOT} [data-mm-subscription-picker]`);
  }

  function getSellingPlanInput() {
    return document.querySelector(`${ROOT} [data-mm-selling-plan-input]`);
  }

  function isSubscriptionSelected() {
    const radio = getSubscriptionPicker()?.querySelector('[data-purchase-type="subscription"] input[type="radio"]');
    return Boolean(radio?.checked && !radio.disabled);
  }

  function getActiveSellingPlanId() {
    if (!isSubscriptionSelected()) return null;
    return getSubscriptionPicker()?.querySelector('.mm-subscription__plan-select')?.value || null;
  }

  function getSellingPlanMap() {
    const script = document.querySelector(`${ROOT} [data-mm-selling-plans]`);
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.warn('MM Main Product: unable to parse selling plan data.', error);
      return [];
    }
  }

  function getDefaultPlanIndex(allocations, picker) {
    const match = picker?.dataset.defaultPlanMatch?.toLowerCase().trim();
    if (!match) return 0;

    const index = allocations.findIndex((allocation) =>
      allocation.plan_name.toLowerCase().includes(match)
    );

    return index >= 0 ? index : 0;
  }

  function syncSellingPlanToForm(planId = null) {
    const form = getProductForm();
    const input = getSellingPlanInput();
    const planSelect = getSubscriptionPicker()?.querySelector('.mm-subscription__plan-select');
    if (!input || !form) return;

    const resolvedPlanId = planId ?? planSelect?.value;

    if (isSubscriptionSelected() && resolvedPlanId) {
      input.setAttribute('name', 'selling_plan');
      input.setAttribute('form', form.id);
      input.value = String(resolvedPlanId);
    } else {
      input.removeAttribute('name');
      input.value = '';
    }
  }

  function getMoneyFormat() {
    const embedded = getSubscriptionPicker()?.querySelector('[data-mm-money-format]')?.textContent;

    if (embedded) {
      try {
        return JSON.parse(embedded);
      } catch (error) {
        /* fall through */
      }
    }

    return window.themeVariables?.settings?.moneyFormat || '${{amount}}';
  }

  function formatMoney(cents, formattedFallback = '', options = {}) {
    const useFallback = formattedFallback && options.allowFormattedFallback !== false;

    if (useFallback) {
      return formattedFallback
        .replace(/\s+(USD|CAD|EUR|GBP|AUD|NZD)\s*$/i, '')
        .replace(/^(USD|CAD|EUR|GBP|AUD|NZD)\s+/i, '')
        .trim();
    }

    const amount = cents / 100;

    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, getMoneyFormat()).replace(/(\d)\.00(\D*$)/, '$1$2');
    }

    if (window.Shopify?.currency?.active) {
      return new Intl.NumberFormat(window.Shopify.locale || 'en-US', {
        style: 'currency',
        currency: window.Shopify.currency.active,
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    }

    return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
  }

  function updateSubscriptionPickerForVariant(variant) {
    const picker = getSubscriptionPicker();
    if (!picker || !variant?.id) return;

    const variantData = getSellingPlanMap().find((entry) => String(entry.id) === String(variant.id));
    const allocations = variantData?.allocations || [];
    const defaultIndex = getDefaultPlanIndex(allocations, picker);
    const subscriptionRadio = picker.querySelector('[data-purchase-type="subscription"] input[type="radio"]');
    const oneTimeRadio = picker.querySelector('[data-purchase-type="one-time"] input[type="radio"]');
    const planSelect = picker.querySelector('.mm-subscription__plan-select');
    const planLabel = picker.querySelector('[data-mm-plan-label]');
    const subscriptionPrice = picker.querySelector('[data-mm-subscription-price]');
    const oneTimePrice = picker.querySelector('[data-mm-onetime-price]');
    const keepSubscription = isSubscriptionSelected();

    if (oneTimePrice) {
      oneTimePrice.textContent = formatMoney(
        lineSubtotalCents(variant.price, 1),
        variantData?.price_formatted
      );
    }

    if (!allocations.length) {
      subscriptionRadio?.setAttribute('disabled', 'disabled');
      oneTimeRadio?.click();
      if (planSelect) planSelect.innerHTML = '';
      if (planLabel) planLabel.textContent = '';
      syncSellingPlanToForm();
      return;
    }

    subscriptionRadio?.removeAttribute('disabled');

    let selectedPlanId = null;

    if (planSelect) {
      planSelect.innerHTML = '';

      allocations.forEach((allocation, index) => {
        const option = document.createElement('option');
        option.value = String(allocation.plan_id);
        option.dataset.price = allocation.price;
        if (allocation.price_formatted) {
          option.dataset.priceFormatted = allocation.price_formatted;
        }
        option.textContent = allocation.plan_name;
        option.selected = index === defaultIndex;
        planSelect.appendChild(option);
      });

      const selectedAllocation = allocations[defaultIndex];
      selectedPlanId = selectedAllocation?.plan_id ?? null;

      if (selectedPlanId) {
        planSelect.value = String(selectedPlanId);
      }

      if (subscriptionPrice && selectedAllocation) {
        subscriptionPrice.textContent = formatMoney(
          lineSubtotalCents(selectedAllocation.price, 1),
          selectedAllocation.price_formatted
        );
      }

      if (planLabel && selectedAllocation) {
        planLabel.textContent = selectedAllocation.plan_name;
      }
    }

    if (keepSubscription || !oneTimeRadio?.checked) {
      subscriptionRadio.checked = true;
    }

    syncSellingPlanToForm(selectedPlanId);
  }

  function patchCartAddFetch() {
    if (window.__mmSellingPlanFetchPatched) return;
    window.__mmSellingPlanFetchPatched = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = function (input, init = {}) {
      const url = typeof input === 'string' ? input : input?.url;

      if (
        url &&
        url.includes('/cart/add') &&
        init.body instanceof FormData &&
        init.body.get('mm_main_product_cart') === '1' &&
        document.querySelector(ROOT) &&
        getProductForm()
      ) {
        syncSellingPlanToForm();

        const tier = getSelectedQuantityTier();
        if (tier) {
          init.body.set('quantity', String(tier.quantity));
        }

        const planId = getActiveSellingPlanId();
        if (planId) {
          init.body.set('selling_plan', planId);
        } else {
          init.body.delete('selling_plan');
        }
      }

      return nativeFetch(input, init);
    };
  }

  function getSectionsToBundle() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  function dispatchCartChange(cartContent, responseJson) {
    if (responseJson?.sections) {
      cartContent.sections = responseJson.sections;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart: cartContent
        }
      })
    );
  }

  function updateAddToCartState(variant) {
    const form = getProductForm();
    if (!form) return;

    const submitButton = form.querySelector('.buy-buttons [type="submit"]');
    if (!submitButton) return;

    const unavailable = !variant || !variant.available;

    submitButton.disabled = unavailable;
    submitButton.setAttribute('aria-disabled', unavailable ? 'true' : 'false');

    if (unavailable) {
      submitButton.textContent = !variant
        ? submitButton.dataset.unavailableText || 'Unavailable'
        : submitButton.dataset.soldOutText || 'Sold out';
    } else {
      submitButton.textContent = submitButton.dataset.addText || submitButton.textContent;
    }
  }

  function cacheSubmitLabels() {
    const submitButton = getProductForm()?.querySelector('.buy-buttons [type="submit"]');
    if (!submitButton || submitButton.dataset.addText) return;

    submitButton.dataset.addText = submitButton.textContent.trim();
    submitButton.dataset.soldOutText = submitButton.dataset.soldOutText || 'Sold out';
    submitButton.dataset.unavailableText = submitButton.dataset.unavailableText || 'Unavailable';
  }

  function getActiveVariant() {
    const script = document.querySelector(`${ROOT} variant-picker script[data-variant]`);
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || 'null');
    } catch (error) {
      return null;
    }
  }

  function getQuantityTierPicker() {
    return document.querySelector(`${ROOT} [data-mm-quantity-tier-picker]`);
  }

  function hasQuantityTierPicker() {
    return Boolean(getQuantityTierPicker());
  }

  function getQuantityTierConfig() {
    const script = getQuantityTierPicker()?.querySelector('[data-mm-quantity-tiers]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.warn('MM Main Product: unable to parse quantity tier data.', error);
      return [];
    }
  }

  function getVolumeDiscountConfig() {
    const script = getQuantityTierPicker()?.querySelector('[data-mm-volume-discounts]');
    if (!script) return { enabled: false, tiers: [] };

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('MM Main Product: unable to parse volume discount data.', error);
      return { enabled: false, tiers: [] };
    }
  }

  function getVolumeDiscountPercent(quantity) {
    const config = getVolumeDiscountConfig();
    if (!config.enabled) return 0;

    const qty = Math.max(1, parseInt(String(quantity), 10) || 1);
    let bestPercent = 0;

    for (const tier of config.tiers || []) {
      const minQuantity = parseInt(String(tier.min_quantity), 10) || 0;
      const percent = parseInt(String(tier.percent), 10) || 0;

      if (minQuantity > 0 && percent > 0 && qty >= minQuantity) {
        bestPercent = Math.max(bestPercent, percent);
      }
    }

    return bestPercent;
  }

  /** Full line subtotal before automatic volume discount (unit cents × cart quantity). */
  function lineSubtotalCents(unitPriceCents, quantity) {
    const qty = Math.max(1, parseInt(String(quantity), 10) || 1);
    const unitCents = Math.max(0, parseInt(String(unitPriceCents), 10) || 0);
    return unitCents * qty;
  }

  /**
   * Shopify automatic “% off each eligible item” on a line:
   * per-unit discount cents = floor(unit × percent ÷ 100), then sum units.
   */
  function discountedLineCents(unitPriceCents, quantity) {
    const qty = Math.max(1, parseInt(String(quantity), 10) || 1);
    const unitCents = Math.max(0, parseInt(String(unitPriceCents), 10) || 0);
    const percent = getVolumeDiscountPercent(qty);

    if (!percent) {
      return lineSubtotalCents(unitCents, qty);
    }

    const discountPerUnitCents = Math.floor((unitCents * percent) / 100);
    return (unitCents - discountPerUnitCents) * qty;
  }

  function getProductVariantData() {
    const map = getSellingPlanMap();
    if (map.length > 0) {
      return map[0];
    }

    const variant = getActiveVariant();
    if (!variant?.id) return null;

    return {
      id: variant.id,
      price: variant.price,
      price_formatted: null,
      available: variant.available,
      allocations: []
    };
  }

  function findAllocationByMatch(allocations, planMatch) {
    const match = planMatch?.toLowerCase().trim();
    if (!match || !allocations?.length) return null;

    return (
      allocations.find((allocation) => allocation.plan_name.toLowerCase().includes(match)) ||
      allocations[0]
    );
  }

  function setFormQuantity(quantity) {
    const tierPicker = getQuantityTierPicker();
    const form = getProductForm();
    if (!tierPicker || !form) return;

    const qty = Math.max(1, parseInt(String(quantity), 10) || 1);
    const tierInput = tierPicker.querySelector('[data-mm-tier-quantity]');
    const useNamedQuantity = tierPicker.dataset.useNamedQuantity !== 'false';

    if (tierInput) {
      tierInput.value = String(qty);
      if (useNamedQuantity) {
        tierInput.setAttribute('name', 'quantity');
        tierInput.setAttribute('form', form.id);
      }
    }

    const quantitySelector = document.querySelector(`${ROOT} quantity-selector`);
    if (quantitySelector?.quantity != null) {
      quantitySelector.quantity = qty;
    } else {
      const quantityInput = form.querySelector('input[name="quantity"]');
      if (quantityInput && quantityInput !== tierInput) {
        quantityInput.value = String(qty);
      }
    }
  }

  function updateTierSelectionVisuals(selectedRadio) {
    const picker = getQuantityTierPicker();
    if (!picker || !selectedRadio) return;

    picker.querySelectorAll('.mm-supply__option').forEach((label) => {
      label.classList.remove('is-selected');
    });

    const label = selectedRadio.closest('.mm-supply__item')?.querySelector('.mm-supply__option');
    label?.classList.add('is-selected');
  }

  function resolveTierFromRadio(radio) {
    if (!radio) return null;

    const tiers = getQuantityTierConfig();
    const tierIndex = parseInt(radio.value, 10);
    const tierFromConfig = tiers.find((entry) => entry.index === tierIndex);

    if (tierFromConfig) {
      return tierFromConfig;
    }

    return {
      index: tierIndex,
      quantity: parseInt(radio.dataset.tierQuantity, 10) || 1,
      plan_match: radio.dataset.tierPlanMatch || ''
    };
  }

  function updatePurchaseOptionPrices(tier, variantData) {
    const picker = getSubscriptionPicker();
    if (!picker || !variantData || !tier) return;

    const quantity = Math.max(1, parseInt(String(tier.quantity), 10) || 1);
    const oneTimePrice = picker.querySelector('[data-mm-onetime-price]');
    const subscriptionPrice = picker.querySelector('[data-mm-subscription-price]');
    const allocations = variantData.allocations || [];
    const allocation = findAllocationByMatch(allocations, tier.plan_match);
    const planSelect = picker.querySelector('.mm-subscription__plan-select');
    const planLabel = picker.querySelector('[data-mm-plan-label]');

    if (oneTimePrice) {
      oneTimePrice.textContent = formatMoney(
        discountedLineCents(variantData.price, quantity),
        '',
        { allowFormattedFallback: false }
      );
    }

    if (!allocation) {
      syncSellingPlanToForm();
      return;
    }

    if (planSelect) {
      planSelect.value = String(allocation.plan_id);
    }

    if (planLabel) {
      planLabel.textContent = allocation.plan_name;
    }

    if (subscriptionPrice) {
      subscriptionPrice.textContent = formatMoney(
        discountedLineCents(allocation.price, quantity),
        '',
        { allowFormattedFallback: false }
      );
    }

    syncSellingPlanToForm(allocation.plan_id);
  }

  function refreshTierDrivenPrices() {
    if (!hasQuantityTierPicker()) return;
    updatePurchaseOptionPrices(getSelectedQuantityTier(), getProductVariantData());
  }

  function applyQuantityTier(tier) {
    if (!tier) return;

    setFormQuantity(tier.quantity);
    updatePurchaseOptionPrices(tier, getProductVariantData());
  }

  function handleQuantityTierInput(radio) {
    if (!radio?.matches('input[type="radio"]')) return;

    updateTierSelectionVisuals(radio);
    applyQuantityTier(resolveTierFromRadio(radio));
  }

  function getSelectedQuantityTier() {
    const picker = getQuantityTierPicker();
    if (!picker) return null;

    const selectedRadio = picker.querySelector('input[type="radio"]:checked');
    return resolveTierFromRadio(selectedRadio);
  }

  function initQuantityTiers() {
    const picker = getQuantityTierPicker();
    if (!picker) return;

    const defaultTierIndex = parseInt(picker.dataset.defaultTier || '0', 10);
    const tiers = getQuantityTierConfig();
    let tier =
      tiers.find((entry) => entry.index === defaultTierIndex) ||
      tiers[0] ||
      null;

    const defaultRadio = picker.querySelector(`input[type="radio"][value="${tier?.index}"]`);
    if (defaultRadio) {
      defaultRadio.checked = true;
      updateTierSelectionVisuals(defaultRadio);
    }

    applyQuantityTier(tier);
  }

  function bindEvents() {
    document.addEventListener('change', (event) => {
      if (!event.target.closest(`${ROOT} [data-mm-subscription-picker]`)) return;

      const picker = getSubscriptionPicker();
      if (!picker) return;

      if (event.target.matches('input[type="radio"]') && event.target.closest('[data-purchase-type]')) {
        syncSellingPlanToForm();
        if (hasQuantityTierPicker()) {
          applyQuantityTier(getSelectedQuantityTier());
        }
        return;
      }

      if (event.target.matches('.mm-subscription__plan-select')) {
        const option = event.target.options[event.target.selectedIndex];

        if (option) {
          syncSellingPlanToForm(option.value);
        }

        if (hasQuantityTierPicker()) {
          refreshTierDrivenPrices();
          return;
        }

        const planLabel = picker.querySelector('[data-mm-plan-label]');
        const subscriptionPrice = picker.querySelector('[data-mm-subscription-price]');

        if (planLabel && option) {
          planLabel.textContent = option.textContent.trim();
        }

        if (subscriptionPrice && option) {
          subscriptionPrice.textContent = formatMoney(
            Number(option.dataset.price),
            option.dataset.priceFormatted
          );
        }
      }
    });

    document.addEventListener('change', (event) => {
      if (!event.target.closest(`${ROOT} [data-mm-quantity-tier-picker]`)) return;
      handleQuantityTierInput(event.target);
    });

    document.addEventListener(
      'click',
      (event) => {
        const radio = event.target.closest(`${ROOT} [data-mm-quantity-tier-picker] input[type="radio"]`);
        if (!radio) return;

        requestAnimationFrame(() => {
          if (radio.checked) {
            handleQuantityTierInput(radio);
          }
        });
      },
      true
    );

    document.addEventListener('variant:change', (event) => {
      const form = getProductForm();
      if (!form || event.detail?.formId !== form.id) return;

      if (!hasQuantityTierPicker()) {
        updateSubscriptionPickerForVariant(event.detail.variant);
      }

      updateAddToCartState(event.detail.variant);
    });

    const form = getProductForm();
    const rerenderRoot = getRerenderRoot();

    if (form && rerenderRoot) {
      const preserved = new Map();

      form.addEventListener(
        'product:rerender',
        () => {
          preserved.clear();
          rerenderRoot.querySelectorAll('[data-mm-preserve]').forEach((el) => {
            preserved.set(el.getAttribute('data-block-id'), el.innerHTML);
          });
        },
        true
      );

      form.addEventListener('product:rerender', (event) => {
        requestAnimationFrame(() => {
          preserved.forEach((html, blockId) => {
            const target = rerenderRoot.querySelector(`[data-block-id="${blockId}"]`);
            if (target) target.innerHTML = html;
          });

          rerenderRoot.querySelectorAll('[data-block-type="@app"]').forEach((target) => {
            const blockId = target.getAttribute('data-block-id');
            const source = event.detail.htmlFragment?.querySelector?.(`[data-block-id="${blockId}"]`);
            if (source) target.replaceWith(source.cloneNode(true));
          });

          const variant = getActiveVariant();
          if (!hasQuantityTierPicker()) {
            updateSubscriptionPickerForVariant(variant);
          } else {
            applyQuantityTier(getSelectedQuantityTier());
          }
          cacheSubmitLabels();
          updateAddToCartState(variant || getProductVariantData());
        });
      });
    }

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target.closest(`${ROOT} .mm-bundle-form`);
        if (!form) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        handleBundleAdd(form, event.submitter || form.querySelector('[type="submit"]'));
      },
      true
    );
  }

  async function handleBundleAdd(form, submitButton) {
    if (!submitButton || submitButton.getAttribute('aria-busy') === 'true') return;

    const includePrimary = form.dataset.includePrimary !== 'false';
    const primaryId = form.dataset.primaryVariant;
    const secondaryId = form.dataset.secondaryVariant;
    const quantity = parseInt(form.dataset.quantity || '1', 10);

    if (!secondaryId) return;
    if (includePrimary && !primaryId) return;

    submitButton.setAttribute('aria-busy', 'true');
    submitButton.disabled = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const root = window.Shopify?.routes?.root || '/';
    const sectionsToBundle = getSectionsToBundle();

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            ...(includePrimary ? [{ id: Number(primaryId), quantity }] : []),
            { id: Number(secondaryId), quantity }
          ],
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname
        })
      });

      const responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      if (!response.ok || responseJson.status) {
        console.error('Bundle add error:', responseJson.description || responseJson.message);
        return;
      }

      if (
        window.themeVariables &&
        (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart')
      ) {
        window.location.href = `${root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${root}cart.js`)).json();
      dispatchCartChange(cartContent, responseJson);
    } catch (error) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      console.error('Bundle add error:', error);
    } finally {
      submitButton.removeAttribute('aria-busy');
      submitButton.disabled = false;
    }
  }

  function init() {
    patchCartAddFetch();
    cacheSubmitLabels();
    bindEvents();
    initQuantityTiers();

    const variant = getActiveVariant() || getProductVariantData();
    if (!hasQuantityTierPicker()) {
      updateSubscriptionPickerForVariant(variant);
    }
    updateAddToCartState(variant);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
